import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, X, Clock } from 'lucide-react';
import MainLayout from '../../components/layout/MainLayout';
import ObituaryCard from '../../components/obituary/ObituaryCard';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import ErrorState from '../../components/common/ErrorState';
import { CardGridSkeleton } from '../../components/common/Skeleton';
import { useToast } from '../../context/ToastContext';
import { fetchObituaries } from '../../utils/api';

const RECENT_KEY = 'obituaryRecentSearches';

/**
 * ObituarySearchPage - 부고 검색 전용 페이지
 * 고인명/장례식장/상주/주소 부분 검색, URL(?q=) 연동, 최근 검색어
 */
const ObituarySearchPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();

  const [obituaries, setObituaries] = useState([]);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '');
  const [submittedQuery, setSubmittedQuery] = useState(searchParams.get('q') || '');
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [hasSearched, setHasSearched] = useState(!!searchParams.get('q'));
  const [sortBy, setSortBy] = useState('latest');
  const [recentSearches, setRecentSearches] = useState([]);

  // 최근 검색어 불러오기
  useEffect(() => {
    try {
      const saved = localStorage.getItem(RECENT_KEY);
      if (saved) setRecentSearches(JSON.parse(saved));
    } catch (e) {
      console.error('Failed to parse recent searches:', e);
    }
  }, []);

  const persistRecent = useCallback((query) => {
    setRecentSearches((prev) => {
      const next = [query, ...prev.filter((q) => q !== query)].slice(0, 6);
      try {
        localStorage.setItem(RECENT_KEY, JSON.stringify(next));
      } catch (e) {
        /* ignore quota errors */
      }
      return next;
    });
  }, []);

  // 부고 목록 불러오기 (의존성 명시 → stale closure 방지)
  const loadObituaries = useCallback(async () => {
    try {
      setIsLoading(true);
      setIsError(false);
      const data = await fetchObituaries();
      setObituaries(data);
    } catch (error) {
      console.error('Failed to load obituaries:', error);
      setIsError(true);
      showToast('부고 목록을 불러오는데 실패했습니다.', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadObituaries();
  }, [loadObituaries]);

  // 검색 결과는 파생값으로 계산 (stale closure / 수동 동기화 제거)
  const searchResults = useMemo(() => {
    const q = submittedQuery.trim().toLowerCase();
    if (!q) return [];

    const filtered = obituaries.filter(
      (o) =>
        o.deceasedName.toLowerCase().includes(q) ||
        o.funeralHome.toLowerCase().includes(q) ||
        o.chiefMourner.toLowerCase().includes(q) ||
        o.funeralAddress.toLowerCase().includes(q)
    );

    return [...filtered].sort((a, b) => {
      if (sortBy === 'name') return a.deceasedName.localeCompare(b.deceasedName, 'ko');
      if (sortBy === 'departure') return new Date(a.departureDate) - new Date(b.departureDate);
      return new Date(b.createdAt) - new Date(a.createdAt);
    });
  }, [obituaries, submittedQuery, sortBy]);

  // 검색 실행
  const runSearch = useCallback(
    (query) => {
      const trimmed = (query ?? '').trim();
      if (!trimmed) {
        showToast('검색어를 입력해주세요.', 'error');
        return;
      }
      setSubmittedQuery(trimmed);
      setHasSearched(true);
      setSearchParams({ q: trimmed });
      persistRecent(trimmed);
    },
    [showToast, setSearchParams, persistRecent]
  );

  const handleSearch = (e) => {
    e.preventDefault();
    runSearch(searchTerm);
  };

  const handleRecentClick = (query) => {
    setSearchTerm(query);
    runSearch(query);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setSubmittedQuery('');
    setHasSearched(false);
    setSearchParams({});
  };

  const handleToggleFavorite = (id) => {
    setObituaries((prev) =>
      prev.map((obit) => (obit.id === id ? { ...obit, isFavorite: !obit.isFavorite } : obit))
    );
  };

  const handleViewDetail = (obituaryId) => navigate(`/obituaries/${obituaryId}`);
  const handleSendWreath = (obituaryId, obituary) =>
    navigate('/wreaths', { state: { obituaryId, obituary } });

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-primary-900 mb-2">부고 검색</h1>
          <p className="text-gray-600">고인명, 장례식장명, 상주명, 주소로 검색하세요.</p>
        </div>

        {/* Search Form */}
        <form onSubmit={handleSearch} className="mb-4">
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Input
                type="text"
                placeholder="고인명, 장례식장명, 상주명, 주소로 검색..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={20}
              />
            </div>
            <Button type="submit" variant="primary" className="px-8">
              검색
            </Button>
            {hasSearched && (
              <Button type="button" variant="outline" onClick={handleClearSearch}>
                초기화
              </Button>
            )}
          </div>
        </form>

        {/* 최근 검색어 */}
        {recentSearches.length > 0 && (
          <div className="mb-8 flex flex-wrap items-center gap-2">
            <span className="flex items-center gap-1 text-sm text-gray-500">
              <Clock size={14} /> 최근 검색어
            </span>
            {recentSearches.map((q) => (
              <button
                key={q}
                type="button"
                onClick={() => handleRecentClick(q)}
                className="inline-flex items-center gap-1 rounded-full bg-accent-100 px-3 py-1 text-sm text-accent-700 hover:bg-accent-200 transition-colors"
              >
                {q}
              </button>
            ))}
            <button
              type="button"
              onClick={() => {
                setRecentSearches([]);
                localStorage.removeItem(RECENT_KEY);
              }}
              className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600"
              aria-label="최근 검색어 전체 삭제"
            >
              <X size={12} /> 전체삭제
            </button>
          </div>
        )}

        {/* Search Results */}
        {isLoading ? (
          <CardGridSkeleton count={6} columns={3} withImage={false} />
        ) : isError ? (
          <ErrorState
            title="부고를 불러오지 못했습니다"
            message="잠시 후 다시 시도해주세요."
            onRetry={loadObituaries}
          />
        ) : hasSearched ? (
          <>
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <p className="text-gray-700" aria-live="polite">
                <span className="font-semibold text-primary-900">"{submittedQuery}"</span>
                에 대한 검색 결과: 총 {searchResults.length}개
              </p>
              {searchResults.length > 0 && (
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  aria-label="정렬 기준"
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="latest">최신순</option>
                  <option value="name">이름순</option>
                  <option value="departure">발인 임박순</option>
                </select>
              )}
            </div>

            {searchResults.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-gray-500 text-lg mb-4">검색 결과가 없습니다.</p>
                <p className="text-sm text-gray-600 mb-6">다른 검색어로 다시 시도해보세요.</p>
                <Button variant="outline" onClick={handleClearSearch}>
                  검색 초기화
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {searchResults.map((obituary) => (
                  <ObituaryCard
                    key={obituary.id}
                    obituary={obituary}
                    onFavoriteToggle={handleToggleFavorite}
                    onViewDetail={() => handleViewDetail(obituary.id)}
                    onSendWreath={(id, obit) => handleSendWreath(id, obit)}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-16">
            <Search className="mx-auto mb-4 text-gray-400" size={64} />
            <p className="text-gray-500 text-lg mb-2">검색어를 입력하고 검색 버튼을 눌러주세요.</p>
            <p className="text-sm text-gray-600">
              고인명, 장례식장명, 상주명 또는 주소로 검색할 수 있습니다.
            </p>
          </div>
        )}

        {/* Info */}
        <div className="mt-8 p-4 bg-accent-50 rounded-lg border border-accent-200">
          <p className="text-sm text-gray-700 text-center">
            <span className="font-semibold">팁:</span> 부분 검색이 가능합니다. 예: "서울" 검색 시 서울 지역의 모든 부고가 표시됩니다.
          </p>
        </div>
      </div>
    </MainLayout>
  );
};

export default ObituarySearchPage;
